import React from 'react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { UserGroupIcon, UserPlusIcon, UserMinusIcon } from '@heroicons/react/24/outline';

const MemberReport = ({ data }) => {
  const summary = data.summary || data;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold">Member Summary Report</h2>
        <p className="text-gray-600">Generated on {formatDate(new Date())}</p>
      </div>

      {/* Member Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
          <UserGroupIcon className="h-8 w-8 mb-2 opacity-90" />
          <div className="text-sm opacity-90">Total Members</div>
          <div className="text-2xl font-bold">{summary.total_members || 0}</div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
          <UserPlusIcon className="h-8 w-8 mb-2 opacity-90" />
          <div className="text-sm opacity-90">Active Members</div>
          <div className="text-2xl font-bold">{summary.active_members || 0}</div>
        </div>
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-4 text-white">
          <UserMinusIcon className="h-8 w-8 mb-2 opacity-90" />
          <div className="text-sm opacity-90">Pending Approval</div>
          <div className="text-2xl font-bold">{summary.pending_members || 0}</div>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
          <div className="text-sm opacity-90">New Members (30 days)</div>
          <div className="text-2xl font-bold">{summary.new_members || 0}</div>
        </div>
      </div>

      {/* Gender Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-3">Gender Distribution</h3>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Male</span>
                <span>{summary.male_members || 0} ({Math.round((summary.male_members / summary.total_members) * 100)}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 rounded-full h-2"
                  style={{ width: `${(summary.male_members / summary.total_members) * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Female</span>
                <span>{summary.female_members || 0} ({Math.round((summary.female_members / summary.total_members) * 100)}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-pink-600 rounded-full h-2"
                  style={{ width: `${(summary.female_members / summary.total_members) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-3">Income Statistics</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Average Monthly Income</span>
              <span className="font-semibold">{formatCurrency(summary.avg_income || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Monthly Income</span>
              <span className="font-semibold">{formatCurrency((summary.avg_income || 0) * (summary.total_members || 0))}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Joining Trend */}
      {data.monthly_joining && data.monthly_joining.length > 0 && (
        <div className="border rounded-lg p-4 mb-6">
          <h3 className="font-semibold mb-3">Monthly Joining Trend (Last 12 Months)</h3>
          <div className="space-y-3">
            {(data.monthly_joining || []).map((month, index) => (
              <div key={index}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{month.month}</span>
                  <span>{month.new_members} members</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 rounded-full h-2"
                    style={{ width: `${(month.new_members / Math.max(...data.monthly_joining.map(m => m.new_members))) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Branch-wise Breakdown */}
      {data.branch_breakdown && data.branch_breakdown.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <h3 className="font-semibold p-4 border-b">Branch-wise Member Distribution</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Members</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">% of Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.branch_breakdown.map((branch, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {branch.branch_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {branch.total_members}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {branch.active_members}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Math.round((branch.total_members / summary.total_members) * 100)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberReport;